import { FileText, Users, Search, UserCheck, CalendarDays } from "lucide-react";

const ArticleCurriculum = () => {
  const curriculum = [
    {
      week: "Week 1",
      title: "Branding, Resume & LinkedIn",
      icon: <FileText className="w-5 h-5 text-blue-400" />,
      sessions: [
        {
          title: "Branding, Resume & LinkedIn",
          description:
            "Build a personal brand, craft a standout resume, and create a powerful profile.",
        },
        {
          title: "Personal brand, standout resume, powerful profile",
          description:
            "A unique profile that stands out and leaves a strong impression.",
        },
      ],
    },
    {
      week: "Week 2",
      title: "Master Networking",
      icon: <Users className="w-5 h-5 text-indigo-400" />,
      sessions: [
        {
          title: "Outreach & Networking",
          description:
            "Learn cold mailing strategies and how to build genuine connections.",
        },
        {
          title: "Master cold mailing, build genuine connections",
          description:
            "A strong, trusted network that creates real opportunities.",
        },
      ],
    },
    {
      week: "Week 3",
      title: "Leverage Industry Insights",
      icon: <Search className="w-5 h-5 text-purple-400" />,
      sessions: [
        {
          title: "Industry Sessions & Referrals",
          description:
            "Learn directly from industry experts and access referral networks.",
        },
        {
          title: "Learn directly from the pros, tap referral networks",
          description: "Practical insights and referral pathways to get ahead.",
        },
      ],
    },
    {
      week: "Week 4",
      title: "Interview & Opportunity Readiness",
      icon: <UserCheck className="w-5 h-5 text-emerald-400" />,
      sessions: [
        {
          title: "Interview Prep & Opportunity Alerts",
          description:
            "Prepare for interviews and quickly act on new openings.",
        },
        {
          title: "Crack interviews, act quickly on fresh openings",
          description:
            "Confidence to perform in interviews and capitalize on fresh openings.",
        },
      ],
    },
  ];

  const techStack = ["Branding", "Networking", "Referrals", "Interview Prep"];

  return (
    <div className="bg-black py-12 px-4 relative overflow-hidden">
      {/* Tech stack floating animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 text-blue-400/10 font-mono text-sm animate-pulse">
          {`function success() { return hardWork + smartStrategy; }`}
        </div>
        <div className="absolute bottom-20 right-10 text-green-400/10 font-mono text-sm animate-pulse">
          {`while(learning) { skills++; }`}
        </div>
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="flex items-center justify-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
            <CalendarDays className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-semibold text-blue-400 tracking-wide uppercase">
              ArticleShip Curriculum
            </span>
          </div>
        </div>

        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              The Journey at a{" "}
              <span className="text-transparent bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text">
                Glance
              </span>
            </h2>
            {/* Tech stack badges */}
            <div className="flex flex-wrap justify-center gap-2 mt-6 max-w-2xl mx-auto">
              {techStack.map((tech, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-800/50 rounded-full text-xs font-mono text-gray-400 border border-gray-700 hover:border-blue-500/50 hover:text-blue-400 transition-all"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="relative space-y-8 py-2 px-2 md:px-0 before:absolute before:inset-0 before:left-[15px] md:before:left-1/2 before:ml-0.5 md:before:ml-0 before:w-0.5 before:bg-gradient-to-b before:from-blue-500 before:via-indigo-500 before:to-purple-500 before:opacity-20">
            {curriculum.map((week, index) => (
              <div
                key={index}
                className={`relative flex items-start ${
                  index % 2 === 0 ? "md:flex-row-reverse" : ""
                }`}
              >
                {/* Timeline dot with enhanced styling */}
                <div className="absolute left-0 md:left-1/2 transform -translate-x-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 border-2 border-blue-400 z-10 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  {week.icon}
                </div>

                {/* Content card with enhanced styling */}
                <div
                  className={`ml-16 md:ml-0 ${
                    index % 2 === 0 ? "md:mr-12 md:pr-16" : "md:ml-12 md:pl-16"
                  } 
                         w-full md:w-2/5 group relative bg-gradient-to-br from-gray-900/50 to-black border border-blue-500/30 rounded-xl p-6 
                         hover:border-blue-400 transition-all duration-300 
                         hover:bg-gradient-to-br hover:from-blue-900/20 hover:to-indigo-900/20
                         hover:shadow-lg hover:shadow-blue-500/10`}
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl blur opacity-0 group-hover:opacity-20 transition duration-300" />
                  <div className="relative text-left">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-bold text-blue-400 text-sm bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/30">
                        {week.week}
                      </div>
                      <div className="px-2 py-1 bg-gray-800/50 rounded text-xs font-mono text-gray-400 border border-gray-700">
                        Module
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-4 text-white group-hover:text-blue-300 transition-colors">
                      {week.title}
                    </h3>
                    <div className="space-y-4">
                      {week.sessions.map((session, sessionIndex) => (
                        <div key={sessionIndex} className="relative">
                          <div className="border-l-2 border-blue-500/30 pl-4 hover:border-blue-400/50 transition-colors">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                              <h4 className="font-semibold text-white text-sm">
                                {sessionIndex === 0
                                  ? "Focus Area"
                                  : "Key Outcomes"}
                                :
                              </h4>
                            </div>
                            <h5 className="font-bold text-blue-300 mb-2 text-sm">
                              {session.title}
                            </h5>
                            <p className="text-gray-400 text-sm group-hover:text-gray-200 transition-colors leading-relaxed">
                              {session.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Compelling Call to Action Ending */}
          {/* Subtle Impact Ending */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-gray-900/80 to-black border border-gray-700/50 backdrop-blur-sm">
              <div className="flex gap-1">
                {techStack.map((_, index) => (
                  <div
                    key={index}
                    className="w-2 h-2 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full animate-pulse"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  />
                ))}
              </div>
              <span className="text-gray-300 font-medium text-sm">
                From Hopeful to{" "}
                <span className="text-blue-400 font-semibold">Hired</span>
              </span>
              <div className="w-px h-4 bg-gray-600" />
              <span className="text-xs text-gray-500 font-bold">4 weeks</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleCurriculum;
